import { parseFullRangeAddress } from 'shared/excel/excel';
import { timeConverter, dateConverter, dateTimeConverter } from 'shared/excel/converters';
import { RangeAddress } from 'shared/excel/rangeAddress';

export class SubstitutionsError extends Error {
  constructor(errors) {
    super("Errors occurred during the substitution")
    this.errors = errors;
  }
}


export function parseSubstitutionExpression(substitutionString) {
  const tableMatch = substitutionString.match(/^@([^\[]+)\[([^\]]+)]$/);
  if (tableMatch) {
    const [ , tableName, columnName] = tableMatch;
    return { table: { name: tableName, columnName }};
  }

  const userMatch = substitutionString.match(/^\$(.+)$/);
  if (userMatch) {
    const [ , userInfo] = userMatch;
    return { userInfo: userInfo.split('.') };
  }

  try {
    const address = new RangeAddress(substitutionString);
    const { worksheetName } = address;
    return { range: { worksheetName, address }};
  }
  catch (error) {
    const { sheet, address } = parseFullRangeAddress(substitutionString);
    return { range: { worksheetName: sheet, name: address } };
  }
}

export function replaceByValues(query, resolvedSubstitutions) {
  // console.log('Query: ', query);
  // console.log('Values: ', substitutions);

  const res = [];
  let queryIndex = 0;
  for (const { value, index, length } of resolvedSubstitutions) {
    res.push(query.slice(queryIndex, index));
    res.push(value);
    queryIndex = index + length;
  }

  if (queryIndex !== query.length) {
    res.push(query.slice(queryIndex, query.length));
  }

  return res.join('');
}


const RangeValueType = {
  Double: 'Double',
  String: 'String',
};

export function convertValue({ value, valueType, numberFormat }) {
  switch (valueType) {
    case RangeValueType.Double: {
      numberFormat = numberFormat.toLowerCase();
      if (numberFormat === 'general' || value < 0) { return { value }; }

      const daysCount = Math.floor(value);
      const dayFraction = value - daysCount;

      if (numberFormat.includes('yy') && numberFormat.includes('d') && numberFormat.includes('m')) {
        return !!dayFraction ? dateTimeConverter(value) : dateConverter(value);
      }

      if (!daysCount && numberFormat.includes('h') && numberFormat.includes('mm')) {
        return timeConverter(value);
      }

      return { value };
    }
    case RangeValueType.String:
    default: return { value, needQuote: true };
  }
}


export function hasSubstitutions(value) {
  const regExp = /{{.+?}}/g;
  const match = regExp.exec(value);
  return !!match;
}


export function tryParseSubstitution(value) {
  const regExp = /{{(.+?)}}/gi;
  const substitutions = [];
  let match = null;

  while ((match = regExp.exec(value))) {
    const [fullMatch, substitution] = match;
    try {
      const expression = parseSubstitutionExpression(substitution);
      substitutions.push({ 
        index: match.index, length: fullMatch.length, substitutionString: fullMatch, expression,
      });
    }
    catch (error) {
      // do nothing
    }
  }

  return {
    substitutions, hasSubstitutions: !!substitutions.length, 
    isPureSubstitution: substitutions.length === 1 && substitutions[0].length === value.length,
  }; 
}


export async function loadSubstitutions(expressions, { tablesLoader, rangesLoader }) {
  const tables = [];
  const groupedRanges = [];
  const userInfos = [];
  
  const results = expressions.map((expression, index) => {
    const { table, range, userInfo } = expression;
    
    table && tables.push({ index, table });
    userInfo && userInfos.push({ index, userInfo });

    if (range) {
      const { worksheetName } = range;
      (groupedRanges.find(x => x.worksheetName === worksheetName)
        ?? (groupedRanges.push({ worksheetName, substitutions: [], }), groupedRanges[groupedRanges.length - 1]))
      .substitutions.push({ index, range });
    } 
    
    return {};
  });

  
  const loadAndAssign = async (toLoad, loader) => {
    if (!toLoad.length) { return; }
    const loadedExpressions = await loader(toLoad);
    loadedExpressions.forEach(({ substitution, range, ranges, error }) => {
      const { index } = substitution;
      // console.log('LoadedExpressions: ', substitution, range, error);

      if (error) {
        results[index] = { error };
        return;
      }
      
      const loadedRanges = ranges || [range];

      const groupedProps = loadedRanges.flatMap(loadedRange =>
        loadedRange.values.flatMap((row, rowIndex) =>
          row.map((value, columnIndex) => ({
            value,
            numberFormat: loadedRange.numberFormat[rowIndex][columnIndex],
            valueType: loadedRange.valueTypes[rowIndex][columnIndex],
          }))
        ));

      results[index] = { range: groupedProps };
    });
  }

  await loadAndAssign(tables, tablesLoader);
  await loadAndAssign(groupedRanges, rangesLoader);

  return results;
}